import React, { useState, useEffect } from 'react';
import ApplicationModal from './ApplicationModal';
import '../styles/ApplicationReview.css';
import { 
  APPLICATION_STAGES, 
  STAGE_TRANSITIONS,
  getStageColor,
  updateApplicationStage 
} from '../utils/applicationStages';
import { useAuth } from '../contexts/AuthContext';

const ApplicationReview = () => {
  console.log('ApplicationReview Component Rendering');
  const [applications, setApplications] = useState([]);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      fetchApplications();
    }
  }, [user]);

  const fetchApplications = async () => {
    try {
      setIsLoading(true);
      console.log('Fetching applications...');
      
      const response = await fetch('/api/applications/pending', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch applications');
      }
      
      const data = await response.json();
      console.log('Fetched applications:', data);
      setApplications(data);
    } catch (error) {
      console.error('Error:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Invalid Date';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleViewApplication = (application) => {
    console.log('Opening modal for application:', application);
    setSelectedApplication(application);
  };

  const handleCloseModal = () => {
    setSelectedApplication(null);
  };

  const handleCreateTestApplication = async () => {
    try {
      console.log('Creating test application...');
      const response = await fetch('/api/applications/test-application', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to create test application');
      }

      const data = await response.json();
      console.log('Created application:', data);
      
      // Refresh the applications list
      await fetchApplications();
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to create test application: ' + error.message);
    }
  };

  const handleStatusChange = async (applicationId, newStatus) => {
    try {
      console.log(`Updating application ${applicationId} to status: ${newStatus}`);
      
      const response = await fetch(`/api/applications/${applicationId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) {
        throw new Error('Failed to update application status');
      }

      // Refresh the applications list
      fetchApplications();
    } catch (error) {
      console.error('Error updating status:', error);
      setError('Failed to update application status');
    }
  };

  if (isLoading) {
    return (
      <div className="loading-pulse">
        <div className="pulse-dot"></div>
        <div className="pulse-dot"></div>
        <div className="pulse-dot"></div>
      </div>
    );
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  const renderStatusBadge = (status) => {
    const stage = Object.values(APPLICATION_STAGES).find(s => 
      s.key.toLowerCase() === status.toLowerCase()
    ) || APPLICATION_STAGES.PENDING;
    return (
      <span 
        className="status-badge"
        style={{
          backgroundColor: getStageColor(stage.key),
          padding: '8px 16px',
          borderRadius: '20px',
          color: 'white',
          fontWeight: '500',
          display: 'inline-block'
        }}
      >
        {stage.label}
      </span>
    );
  };
  const handleStageChange = async (applicationId, newStage) => {
    try {
      await updateApplicationStage(applicationId, newStage);
      // Refresh the applications list
      fetchApplications();
    } catch (error) {
      console.error('Error updating stage:', error);
      setError('Failed to update application stage');
    }
  };

  const renderNextSteps = (application) => {
    const currentStage = application.status.toLowerCase();
    
    const getNextStep = () => {
      switch (currentStage) {
        case 'pending':
          return {
            text: 'Needs Initial Review',
            color: '#666', // Subtle gray
            icon: '' 
          };
        case 'legal_review':
          return {
            text: 'In Legal Review',
            color: '#FFD700', // Gold
            icon: '⚖️'
          };
        case 'info_requested':
          return {
            text: 'Waiting for Response',
            color: '#9B59B6', // Purple to match your REQUEST INFO button
            icon: '⏳'
          };
        case 'approved':
          return {
            text: 'Ready for Onboarding',
            color: '#2ECC71', // Green to match your APPROVE button
            icon: '✅'
          };
        case 'denied':
          return {
            text: 'Application Closed',
            color: '#E74C3C', // Red to match your DENY button
            icon: '❌'
          };
        default:
          return {
            text: 'Pending Review',
            color: '#F39C12', // Orange to match your pending badge
            icon: '📋'
          };
      }
    };

    const nextStep = getNextStep();

    return (
      <div className="action-column">
        <button
          className="view-application-btn"
          onClick={() => handleViewApplication(application)}
        >
          View Application
        </button>
        <div 
          className="next-step-indicator"
          style={{
            display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 12px',
          borderRadius: '4px',
          backgroundColor: 'rgba(147, 112, 219, 0.1)', // Ghostly purple with transparency
          color: '#9370DB', // Solid ghostly purple
          fontSize: '0.9em',
          fontWeight: '500',
          marginTop: '8px',
          border: '1px solid rgba(147, 112, 219, 0.2)' // Subtle border
          }}
        >
          <span className="next-step-icon">{nextStep.icon}</span>
          <span>{nextStep.text}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="application-review">
      <h1>Application Review Queue</h1>
      <div className="applications-table">
        <table>
          <thead>
            <tr>
              <th>Organization</th>
              <th>Contact</th>
              <th>Submitted</th>
              <th>Status</th>
              <th>Next Steps</th>
            </tr>
          </thead>
          <tbody>
            {applications.map((app) => (
              <tr key={app._id}>
                <td>{app.project?.name || 'N/A'}</td>
                <td>
                  <div>{app.applicant?.name || 'N/A'}</div>
                  <div className="email-text">{app.applicant?.email || 'N/A'}</div>
                </td>
                <td>{formatDate(app.metadata?.submittedAt)}</td>
                <td>
                  <span className={`status-badge ${app.status}`}>
                    {app.status}
                  </span>
                </td>
                <td>{renderNextSteps(app)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedApplication && (
        <ApplicationModal
          application={selectedApplication}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

export default ApplicationReview; 